import {
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  StepConnector,
  stepConnectorClasses,
  styled,
} from '@mui/material';
import ConsentIdentityStepIcon from './ConsentIndentityStepIcon';
import { STEPPER_SECTIONS } from './stepperSections';
import { TermDto } from '@act/shared/data-transfer-objects';
import ConsentTermContent from './ConsentTermContent';
import ConsentIdentityForm, {
  ConsentIdentityFormRef,
} from './ConsentIdentityForm';
import React, { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import useBreakpoint from 'utilities/use-breakpoint';

const StyledStepConnector = styled(StepConnector)(() => {
  const { mobile, tablet } = useBreakpoint();
  const isMobile = mobile || tablet;

  return {
    [`& .${stepConnectorClasses.line}`]: {
      position: 'relative',
      top: 7,
      left: '-12px',
      width: isMobile ? 82 + STEPPER_SECTIONS.length * 10 + '%' : '101.5%',
      borderWidth: '1px',
      borderColor: '#021e38',
      borderStyle: 'solid',
    },
    [`&.${stepConnectorClasses.active}, &.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: '#ff7b65',
      },
    },
  };
});

interface ConsentIdentityStepperProps {
  term?: TermDto;
  isLastStep: boolean;
  activeStep: number;
  setActiveStep: (step: number) => void;
  onFormValidityChange?: (isValid: boolean) => void;
}

export interface ConsentIdentityStepperRef {
  submitForm: () => Promise<void>;
}

const ConsentIdentityStepper = forwardRef<
  ConsentIdentityStepperRef,
  ConsentIdentityStepperProps
>(
  (
    { term, isLastStep, activeStep, setActiveStep, onFormValidityChange },
    ref
  ) => {
    const { t } = useTranslation();

    const formRef = React.useRef<ConsentIdentityFormRef>(null);

    useImperativeHandle(
      ref,
      () => ({
        submitForm: async () => {
          if (formRef.current) {
            return await formRef.current.submitForm();
          }
          throw new Error('Form ref not available');
        },
      }),
      []
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
          overflow: 'hidden',
          marginBottom: '16px',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Typography
            variant="subheadLarge"
            sx={{
              textAlign: 'center',
              fontWeight: '400',
              color: '#021e38',
            }}
          >
            {t(STEPPER_SECTIONS[activeStep])}
          </Typography>
        </Box>

        <Stepper
          activeStep={activeStep}
          connector={<StyledStepConnector />}
          sx={{ alignItems: 'flex-start' }}
        >
          {STEPPER_SECTIONS.map(label => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={ConsentIdentityStepIcon}
              ></StepLabel>
            </Step>
          ))}
        </Stepper>
        {!isLastStep && (
          <ConsentIdentityForm
            ref={formRef}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            onFormValidityChange={onFormValidityChange}
          />
        )}
        {isLastStep && <ConsentTermContent term={term} />}
      </Box>
    );
  }
);

export default ConsentIdentityStepper;
